// Baseline architectural scope/quantity list for the Room Detail Flyout —
// computed live from a room's own dimensions + Discipline Criteria (never
// persisted; RoomScopeItems, db/39_room_scope_items.sql, only stores the
// estimator's overrides/dismissals/custom additions on top of this).
//
// Each baseline item has a stable sourceKey RoomScopeItems overrides key
// against — add a new formula here and it shows up automatically, no
// migration needed (same "frontend-only field" philosophy as
// takeoffCriteria.js itself).

import { CRITERIA_DISCIPLINES, effectiveCriterion } from "./takeoffCriteria";
import { polygonOf, polygonArea, polygonPerimeter } from "../utils/takeoffGeometry";

const ARCH = CRITERIA_DISCIPLINES.find(d => d.key === "arch");
const fieldCsi = (key) => ARCH.fields.find(f => f.key === key)?.csi || "";

const COVING_MULTIPLIER = {
  "Full (floor + ceiling)": 2,
  "Floor only": 1,
  "None": 0,
};

// room -> [{ sourceKey, description, unit, quantity, csi }], skipping any
// item with no dependency set yet (no system selected, count is 0, or the
// room has no L/W to derive an area/perimeter from).
//
// Deliberately does NOT auto-generate a wall panel SF line. A room's
// Discipline Criteria wall_system is a single value, but a real room
// routinely mixes systems wall-by-wall (e.g. a 2"-honeycomb partition
// finished on both faces vs. a 1/2" liner finished on one face only,
// against an already-finished neighbor) — a per-room number can't represent
// that without silently being wrong for any room that isn't uniform, and
// there's no way to tell from here which walls are which. Wall panel SF
// needs to be taken off wall-by-wall instead, via the canvas's "Draw
// condition" tool (Condition Library, db/37_condition_library.sql) — each
// drawn run gets its own cost-coded type and length, which is inherently
// per-wall and rolls up correctly (sp_GetConditionRoomRollup) with no
// guessing. wall_system here still describes the room's PREDOMINANT system
// for reference; it just doesn't drive a quantity on its own.
export function computeBaselineRoomScope(room, criteria) {
  const L = +room.LengthFt || 0;
  const W = +room.WidthFt || 0;
  // A room with an AI-traced polygon (non-rectangular boundary) uses its
  // real area/perimeter here instead of L*W/2*(L+W), which only ever
  // describes its bounding box once a real shape exists.
  const poly = polygonOf(room);
  const floorArea = poly ? polygonArea(poly) : L * W;
  const perimeter = poly ? polygonPerimeter(poly) : 2 * (L + W);
  const items = [];

  const projectField = (key) => criteria[`arch|${key}|0`] || "";

  if (floorArea > 0) {
    const ceilingSystem = projectField("ceiling_system");
    if (ceilingSystem && ceilingSystem !== "By others") {
      items.push({
        sourceKey: "ceiling_sf", description: `${ceilingSystem} — Ceiling System`,
        unit: "sf", quantity: Math.round(floorArea * 10) / 10, csi: fieldCsi("ceiling_system"),
      });
    }
    const floorFinish = projectField("floor_finish");
    if (floorFinish && floorFinish !== "By others") {
      items.push({
        sourceKey: "floor_sf", description: `${floorFinish} — Floor Finish`,
        unit: "sf", quantity: Math.round(floorArea * 10) / 10, csi: fieldCsi("floor_finish"),
      });
    }
  }

  if (perimeter > 0) {
    // Unlike wall panel, coving is a per-room interior floor/ceiling-joint
    // finish — this room needs its own coving along its own interior faces
    // regardless of what system (or whether another room) is on the other
    // side of any given wall, so no wall-by-wall precision issue here.
    const coving = projectField("coving");
    const mult = COVING_MULTIPLIER[coving] ?? 0;
    if (mult > 0) {
      items.push({
        sourceKey: "coving_lf", description: `Coving — ${coving}`,
        unit: "lf", quantity: Math.round(perimeter * mult * 10) / 10, csi: fieldCsi("coving"),
      });
    }
  }

  const countField = (fieldKey, sourceKey, label) => {
    const eff = effectiveCriterion(criteria, "arch", fieldKey, room);
    const qty = +eff.value || 0;
    if (qty > 0) items.push({ sourceKey, description: label, unit: "ea", quantity: qty, csi: fieldCsi(fieldKey) });
  };
  countField("door_count", "door_ea", "Personnel Doors");
  countField("window_count", "window_ea", "Vision Panels");
  countField("pass_throughs", "passthrough_ea", "Pass-Throughs");

  return items;
}
